
import { Modal } from 'bootstrap';
import { defineComponent, ref, inject, computed, watchEffect, nextTick } from 'vue';
import { Select, Input } from '@/components/input-elements';
import { Apollo, Notify } from '@/core/services';
import { GET_PRICE_GROUP } from '@/modules/customer/price-groups/graphql/Queries';
import { GET_PRODUCT_GROUP_PRICE_ASSIGNMENTS } from '@/modules/catalog/product-groups/graphql/Queries';
import { CREATE_PRODUCT_GROUP_PRICE_ASSIGNMENTS } from '@/modules/catalog/product-groups/graphql/Mutations';
import { useI18n } from 'vue-i18n';
import InnerLoader from '@/components/InnerLoader.vue';

interface AssignmentItem {
    id?: number;
    price_group: number | null;
    discount_type: string | null;
    discount_value: number | null;
    price_group_name?: string;
}

export default defineComponent({
    name: 'price-group-assignment-modal',
    components: {
        Select,
        // Input,
        InnerLoader
    },
    setup() {
        const modal: any = ref();
        const loading = ref(false);
        const emitter: any = inject('emitter');
        const i18n = useI18n();
        const assignmentForm = ref<null | HTMLFormElement>(null);
        const productGroupId = ref<number | null>(null);
        const priceGroups = ref([]) as Record<any, any>;
        const assignmentList = ref<AssignmentItem[]>([]);
        const priceGroupSelectRef = ref();
        const discountTypeSelectRef = ref();
        const formKey = ref(0);
        const isEditing = ref(false);
        const editingIndex = ref(-1);

        // Assignment form data
        const assignmentData = ref({
            price_group: null as number | null,
            discount_type: null as string | null,
            discount_value: '' as string
        });

        // Discount type options
        const discountTypes = ref([
            { value: 'percentage', label: 'message.PERCENTAGE' },
            { value: 'amount', label: 'message.AMOUNT' }
        ]);

        // Computed property to check if assignment can be added
        const canAddAssignment = computed(() => {
            return assignmentData.value.price_group && assignmentData.value.discount_type && assignmentData.value.discount_value !== '' && parseFloat(assignmentData.value.discount_value) > 0;
        });

        // Computed property to filter out already assigned price groups
        const availablePriceGroups = computed(() => {
            const assignedPriceGroupIds = assignmentList.value.map(assignment => assignment.price_group).filter(id => id !== null);

            // If editing, exclude the currently editing price group from the filter
            if (isEditing.value && editingIndex.value >= 0) {
                const currentEditingPriceGroup = assignmentList.value[editingIndex.value]?.price_group;
                const filteredIds = assignedPriceGroupIds.filter(id => id !== currentEditingPriceGroup);
                const result = priceGroups.value.filter(priceGroup => !filteredIds.includes(priceGroup.id));

                // Debug: Log the available price groups when editing
                console.log('Available price groups when editing:', {
                    currentEditingPriceGroup,
                    filteredIds,
                    result: result.map(pg => ({ id: pg.id, name: pg.name, value: pg.value, label: pg.label }))
                });

                return result;
            }

            return priceGroups.value.filter(priceGroup => !assignedPriceGroupIds.includes(priceGroup.id));
        });

        // Computed property for button text
        const buttonText = computed(() => {
            return isEditing.value ? i18n.t('message.UPDATE_ASSIGNMENT') : i18n.t('message.ADD_ASSIGNMENT');
        });

        // Get price group name by ID
        const getPriceGroupName = (id: number | null) => {
            if (!id) return '';
            const priceGroup = priceGroups.value.find((pg: any) => pg.id === id || pg.value === id);
            return priceGroup ? priceGroup.name : `ID: ${id}`;
        };

        // Get discount type name
        const getDiscountTypeName = (type: string | null) => {
            if (!type) return '';
            const discountType = discountTypes.value.find((dt: any) => dt.value === type);
            return discountType ? i18n.t(discountType.label) : type;
        };

        // Add assignment to list
        const addAssignment = () => {
            if (!canAddAssignment.value) return;

            const newAssignment: AssignmentItem = {
                price_group: assignmentData.value.price_group,
                discount_type: assignmentData.value.discount_type,
                discount_value: parseFloat(assignmentData.value.discount_value),
                price_group_name: getPriceGroupName(assignmentData.value.price_group)
            };

            // Check if assignment already exists (this should not happen with filtered dropdown, but keeping as safety check)
            const exists = assignmentList.value.some(assignment => assignment.price_group === newAssignment.price_group);

            if (exists) {
                Notify.error(i18n.t('message.PRICE_GROUP_ALREADY_ASSIGNED'));
                return;
            }

            assignmentList.value.push(newAssignment);

            // Reset form
            assignmentData.value = {
                price_group: null,
                discount_type: null,
                discount_value: ''
            };

            // Force re-render of form to clear Select components
            formKey.value++;

            Notify.success(i18n.t('message.ASSIGNMENT_ADDED_SUCCESSFULLY'));
        };

        // Edit assignment
        const editAssignment = (assignment: AssignmentItem, index: number) => {
            isEditing.value = true;
            editingIndex.value = index;

            // Debug: Log the values to see what's being set
            console.log('Edit assignment data:', {
                price_group: assignment.price_group,
                discount_type: assignment.discount_type,
                discount_value: assignment.discount_value
            });

            // Force re-render first to clear any previous state
            formKey.value++;

            // Then set the data after a small delay
            setTimeout(() => {
                assignmentData.value = {
                    price_group: assignment.price_group,
                    discount_type: assignment.discount_type,
                    discount_value: assignment.discount_value?.toString() || ''
                };
            }, 100);
        };

        // Cancel edit
        const cancelEdit = () => {
            isEditing.value = false;
            editingIndex.value = -1;

            // Reset form
            assignmentData.value = {
                price_group: null,
                discount_type: null,
                discount_value: ''
            };

            // Force re-render of form to clear Select components
            formKey.value++;
        };

        // Update assignment
        const updateAssignment = () => {
            if (!canAddAssignment.value || editingIndex.value === -1) return;

            const updatedAssignment: AssignmentItem = {
                id: assignmentList.value[editingIndex.value].id, // Keep the original ID
                price_group: assignmentData.value.price_group,
                discount_type: assignmentData.value.discount_type,
                discount_value: parseFloat(assignmentData.value.discount_value),
                price_group_name: getPriceGroupName(assignmentData.value.price_group)
            };

            // Check if assignment already exists (excluding the current one being edited)
            const exists = assignmentList.value.some((assignment, index) => assignment.price_group === updatedAssignment.price_group && index !== editingIndex.value);

            if (exists) {
                Notify.error(i18n.t('message.PRICE_GROUP_ALREADY_ASSIGNED'));
                return;
            }

            // Update the assignment in the list
            assignmentList.value[editingIndex.value] = updatedAssignment;

            // Reset form and editing state
            cancelEdit();
            Notify.success(i18n.t('message.ASSIGNMENT_UPDATED_SUCCESSFULLY'));
        };

        // Remove assignment from list
        const removeAssignment = (index: number) => {
            assignmentList.value.splice(index, 1);
            Notify.success(i18n.t('message.ASSIGNMENT_REMOVED_SUCCESSFULLY'));
        };

        // Reset form data
        const resetForm = () => {
            assignmentData.value = {
                price_group: null,
                discount_type: null,
                discount_value: ''
            };
            assignmentList.value = [];
            isEditing.value = false;
            editingIndex.value = -1;
        };

        // Submit handler
        const submitHandlerForm = async () => {
            loading.value = true;

            try {
                // Prepare the assignments data for the mutation
                const assignmentsData = assignmentList.value.map(assignment => ({
                    price_group_id: assignment.price_group,
                    discount_type: assignment.discount_type,
                    discount_value: assignment.discount_value
                }));

                const formData = {
                    product_group_id: productGroupId.value,
                    assignments: assignmentsData
                };

                console.log('Submitting assignments:', formData);

                await Apollo.mutate({
                    mutation: CREATE_PRODUCT_GROUP_PRICE_ASSIGNMENTS,
                    variables: { input: formData },
                    update: (store, { data: { create_product_group_price_assignments } }) => {
                        if (create_product_group_price_assignments) {
                            modal.value.hide();

                            // Show appropriate success message based on whether assignments were added or cleared
                            if (assignmentList.value.length === 0) {
                                Notify.success(i18n.t('message.ALL_ASSIGNMENTS_CLEARED_SUCCESSFULLY'));
                            } else {
                                Notify.success(i18n.t('message.ASSIGNMENTS_SAVED_SUCCESSFULLY'));
                            }

                            resetForm();
                        }
                    }
                });
            } catch (error) {
                console.error('Error saving assignments:', error);
                Notify.error(i18n.t('message.ERROR_SAVING_ASSIGNMENTS'));
            } finally {
                loading.value = false;
            }
        };

        // Load existing assignments
        const loadExistingAssignments = async (productGroupId: number) => {
            try {
                const result = await Apollo.query({
                    query: GET_PRODUCT_GROUP_PRICE_ASSIGNMENTS,
                    variables: { product_group_id: productGroupId },
                    fetchPolicy: 'network-only', // Always fetch fresh data
                    errorPolicy: 'all'
                });

                if (result.data.product_group_price_assignments) {
                    assignmentList.value = result.data.product_group_price_assignments.map((assignment: any) => ({
                        id: assignment.id,
                        price_group: assignment.price_group_id,
                        discount_type: assignment.discount_type,
                        discount_value: assignment.discount_value,
                        price_group_name: assignment.price_group?.name || ''
                    }));
                } else {
                    assignmentList.value = [];
                }
            } catch (error) {
                console.error('Error loading existing assignments:', error);
                assignmentList.value = [];
                // Don't show error notification as this might be expected for new product groups
            }
        };

        // Load price groups
        const loadPriceGroups = async () => {
            try {
                const result = await Apollo.query({
                    query: GET_PRICE_GROUP,
                    variables: { page: 1, limit: 1000 }
                });

                priceGroups.value = result.data.price_groups.data.map((pg: { id: number; name: string }) => ({
                    id: pg.id,
                    name: pg.name,
                    value: pg.id,
                    label: pg.name
                }));
            } catch (error) {
                console.error('Error loading price groups:', error);
                Notify.error(i18n.t('message.ERROR_LOADING_PRICE_GROUPS'));
            }
        };

        // Refresh assignments data
        const refreshAssignments = async () => {
            if (productGroupId.value) {
                await loadExistingAssignments(productGroupId.value);
            }
        };

        // Watch for changes in assignmentData to debug
        watchEffect(() => {
            if (isEditing.value) {
                console.log('Assignment data changed:', assignmentData.value);
            }
        });

        // Emitter to open modal
        emitter.on('priceGroupAssignmentAction', async (productGroup: { id: number; [key: string]: unknown }) => {
            resetForm();
            productGroupId.value = productGroup.id;
            await loadPriceGroups();
            await loadExistingAssignments(productGroup.id);
            modal.value = new Modal(document.getElementById('modal_price_group_assignment'));
            modal.value.show();
        });

        return {
            loading,
            assignmentData,
            assignmentForm,
            submitHandlerForm,
            priceGroups,
            availablePriceGroups,
            discountTypes,
            assignmentList,
            canAddAssignment,
            buttonText,
            isEditing,
            getPriceGroupName,
            getDiscountTypeName,
            addAssignment,
            editAssignment,
            updateAssignment,
            cancelEdit,
            removeAssignment,
            resetForm,
            priceGroupSelectRef,
            discountTypeSelectRef,
            formKey,
            loadExistingAssignments,
            refreshAssignments
        };
    }
});
